import { Link, useLocation } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { BUSINESS } from '../data/business';
import { isEnglishPath } from '../utils/i18nRoutes';
import LastUpdated from './LastUpdated';

interface FooterLink {
  label: string;
  labelEn: string;
  path: string;
}

const serviceLinks: FooterLink[] = [
  { label: 'KI-Telefonassistent', labelEn: 'AI Phone Assistant', path: '/leistungen/ki-telefonassistent' },
  { label: 'Prozessautomatisierung', labelEn: 'Process Automation', path: '/leistungen/prozessautomatisierung' },
  { label: 'KI-Integration', labelEn: 'AI Integration', path: '/leistungen/ki-integration' },
  { label: 'Kostenoptimierung', labelEn: 'Cost Optimization', path: '/leistungen/kostenoptimierung' },
];

const companyLinks: FooterLink[] = [
  { label: 'Über uns', labelEn: 'About Us', path: '/ueber-uns' },
  { label: 'Referenzen', labelEn: 'References', path: '/referenzen' },
  { label: 'KI-Beratung Wien', labelEn: 'AI Consulting Vienna', path: '/ki-beratung-wien' },
  { label: 'Kontakt', labelEn: 'Contact', path: '/kontakt' },
];

const legalLinks: FooterLink[] = [
  { label: 'Impressum', labelEn: 'Legal Notice', path: '/impressum' },
  { label: 'Datenschutz', labelEn: 'Privacy Policy', path: '/datenschutz' },
  { label: 'Sitemap', labelEn: 'Sitemap', path: '/sitemap' },
];

const linkClass =
  'text-gray-400 hover:text-accent-light transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-dark rounded-sm';

export default function Footer() {
  const location = useLocation();
  const isEn = isEnglishPath(location.pathname);

  const svcLinks = isEn ? serviceLinks.map(l => ({ ...l, label: l.labelEn })) : serviceLinks;
  const cmpLinks = isEn ? companyLinks.map(l => ({ ...l, label: l.labelEn })) : companyLinks;
  const legLinks = isEn ? legalLinks.map(l => ({ ...l, label: l.labelEn })) : legalLinks;

  const labels = {
    company: isEn ? 'Company' : 'Unternehmen',
    services: isEn ? 'Services' : 'Leistungen',
    legal: isEn ? 'Legal' : 'Rechtliches',
    hours: isEn ? 'Hours: Mon–Fri 09:00–18:00' : 'Öffnungszeiten: Mo–Fr 09:00–18:00',
    sitemap: isEn ? 'Sitemap' : 'Sitemap',
    footerLabel: isEn ? `${BUSINESS.shortName} – Footer` : `${BUSINESS.shortName} – Fußzeile`,
  };

  return (
    <footer
      className="bg-dark text-white py-16 px-6"
      aria-label={labels.footerLabel}
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          {/* Column 1 — Company (contact details) */}
          <nav aria-label={labels.company} className="space-y-4">
            <h2 className="font-heading font-bold text-white text-lg">{labels.company}</h2>
            <div className="flex items-center gap-3">
              <picture>
                <source srcSet="/OptiAI_logo_transparent.webp" type="image/webp" />
                <img
                  src="/OptiAI_logo_transparent.png"
                  alt="OptiAI – KI-Integration & Kostenoptimierung"
                  width="160"
                  height="40"
                  loading="lazy"
                  decoding="async"
                  className="h-9 w-auto"
                />
              </picture>
            </div>
            <address
              className="not-italic space-y-3 text-gray-400"
              itemScope
              itemType="https://schema.org/LocalBusiness"
            >
              <meta itemProp="name" content={BUSINESS.name} />
              <meta itemProp="url" content={BUSINESS.url} />
              <div
                itemProp="address"
                itemScope
                itemType="https://schema.org/PostalAddress"
                className="hidden"
              >
                <meta itemProp="streetAddress" content={BUSINESS.address.street} />
                <meta itemProp="postalCode" content={BUSINESS.address.postalCode} />
                <meta itemProp="addressLocality" content={BUSINESS.address.locality} />
                <meta itemProp="addressCountry" content={BUSINESS.address.countryCode} />
              </div>
              <p className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                <span>
                  {BUSINESS.name}
                  <br />
                  {BUSINESS.address.street}, {BUSINESS.address.postalCode}{' '}
                  {BUSINESS.address.locality}, {BUSINESS.address.country}
                </span>
              </p>
              <p className="flex items-center gap-2">
                <Phone className="w-4 h-4 shrink-0" aria-hidden="true" />
                <a href={BUSINESS.phoneHref} itemProp="telephone" className={linkClass}>
                  {BUSINESS.phone}
                </a>
              </p>
              <p className="flex items-center gap-2">
                <Mail className="w-4 h-4 shrink-0" aria-hidden="true" />
                <a href={BUSINESS.emailHref} itemProp="email" className={linkClass}>
                  {BUSINESS.email}
                </a>
              </p>
              <p className="flex items-center gap-2">
                <Clock className="w-4 h-4 shrink-0" aria-hidden="true" />
                <span>{labels.hours}</span>
              </p>
            </address>
          </nav>

          {/* Column 2 — Services */}
          <nav aria-label={labels.services} className="space-y-4">
            <h2 className="font-heading font-bold text-white text-lg">{labels.services}</h2>
            <ul className="space-y-3 text-gray-400">
              {svcLinks.map((item) => (
                <li key={item.path}>
                  <Link to={item.path} className={linkClass}>{item.label}</Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Column 3 — Company (links) */}
          <nav aria-label={labels.company} className="space-y-4">
            <h2 className="font-heading font-bold text-white text-lg">{labels.company}</h2>
            <ul className="space-y-3 text-gray-400">
              {cmpLinks.map((item) => (
                <li key={item.path}>
                  <Link to={item.path} className={linkClass}>{item.label}</Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Column 4 — Legal */}
          <nav aria-label={labels.legal} className="space-y-4">
            <h2 className="font-heading font-bold text-white text-lg">{labels.legal}</h2>
            <ul className="space-y-3 text-gray-400">
              {legLinks.map((item) => (
                <li key={item.path}>
                  <Link to={item.path} className={linkClass}>{item.label}</Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <div className="border-t border-gray-800 pt-8 flex flex-col items-center gap-4 text-gray-400 text-sm">
          <p>&copy; 2026 {BUSINESS.shortName}</p>
          <LastUpdated />
          <Link to="/sitemap" className={linkClass}>
            {labels.sitemap}
          </Link>
        </div>
      </div>
    </footer>
  );
}
